/**
 * List pagination — the wire shape published in `openapi/fiskhub.v1.yaml`.
 *
 * Every list endpoint returns an envelope, not a bare array:
 *
 *   { "object": "list", "data": [...], "has_more": true, "next_cursor": "..." }
 *
 * That is a deliberate exception to the otherwise-unwrapped responses of §7,
 * and it is the contract the client builds against. §7 is explicit that the
 * contract ships before the implementation and takes only additive changes
 * afterwards ("Never break /v1"), so the envelope is fixed: a bare array
 * cannot grow pagination metadata later without breaking every caller.
 *
 * Pagination is KEYSET, not OFFSET. An offset page over a table that is being
 * written to shifts under the reader — a new invoice arriving between page 1
 * and page 2 pushes a row across the boundary and the caller never sees it.
 * For an invoice browser whose whole purpose is "did anything fail", silently
 * skipping a row is the one failure mode we cannot ship.
 */
import { z } from "zod";
/** Page size when the caller does not ask, and the ceiling when it does. */
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;
/**
 * Cursors are OPAQUE by contract ("Never construct one by hand"). The encoding
 * is base64url of a versioned JSON tuple, which is reversible on purpose —
 * opaque means "not part of the contract", not "encrypted". It carries no
 * tenant identity and grants no access: the query it resumes is still scoped
 * by the caller's own credentials and by RLS.
 */
const CURSOR_VERSION = 1;
export function encodeCursor(keys) {
    const payload = { v: CURSOR_VERSION, k: keys };
    return Buffer.from(JSON.stringify(payload), "utf8").toString("base64url");
}
/**
 * Returns `null` for anything we did not issue — malformed base64, foreign
 * JSON, or a cursor from an older encoding.
 *
 * The caller must treat `null` as a client error rather than falling back to
 * the first page. A silent fallback turns a stale cursor into an endless
 * "Next" button that re-serves page 1 forever, which is precisely the bug this
 * module exists to prevent.
 */
export function decodeCursor(cursor) {
    try {
        const parsed = JSON.parse(Buffer.from(cursor, "base64url").toString("utf8"));
        if (typeof parsed !== "object" || parsed === null)
            return null;
        const payload = parsed;
        if (payload.v !== CURSOR_VERSION || !Array.isArray(payload.k))
            return null;
        for (const key of payload.k) {
            if (key !== null && typeof key !== "string" && typeof key !== "number")
                return null;
        }
        return payload.k;
    }
    catch {
        return null;
    }
}
/**
 * Build the envelope from a page that was fetched with ONE EXTRA ROW.
 *
 * `has_more` cannot be inferred from `rows.length === limit`: a page that is
 * exactly full is indistinguishable from the last page, so the caller is
 * handed a `next_cursor` that returns nothing. Over-fetching by one is the
 * cheapest way to know, and far cheaper than a COUNT(*) over a filtered scan.
 */
export function toListEnvelope(rows, limit, toView, cursorOf) {
    const hasMore = rows.length > limit;
    const page = hasMore ? rows.slice(0, limit) : rows;
    const last = page.at(-1);
    return {
        object: "list",
        // `page.map(toView)` would be wrong: `map` passes (row, index, array), and a
        // serialiser with an optional second parameter — `toView(row, now = new
        // Date())` in `services/certificates.ts` — silently receives the index as
        // its `now`. Call it with exactly one argument.
        data: page.map((row) => toView(row)),
        has_more: hasMore,
        next_cursor: hasMore && last !== undefined ? encodeCursor(cursorOf(last)) : null,
    };
}
/** An empty page, for the endpoints that short-circuit before querying. */
export function emptyList() {
    return { object: "list", data: [], has_more: false, next_cursor: null };
}
// -----------------------------------------------------------------------------
// Request side
// -----------------------------------------------------------------------------
/**
 * The two query parameters every list endpoint accepts, contract-first like
 * every other schema in this package (§7).
 *
 * A cursor we did not issue is a `validation_error`, not a silent first page.
 * Falling back would make "Next" re-serve page one forever — the caller sees an
 * endless list of the same rows and no error anywhere to explain it.
 */
export const paginationQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    cursor: z
        .string()
        .max(512)
        .refine((value) => decodeCursor(value) !== null, {
        message: "Not a cursor this API issued. Pass back the `next_cursor` from the previous page verbatim; cursors are opaque and must not be constructed or modified.",
    })
        .optional(),
});
/**
 * Turn validated query parameters into the option bag `@fiskhub/db` list
 * helpers take. `cursor` has already been proven decodable by the schema.
 */
export function keysetFrom(query) {
    const cursor = query.cursor === undefined ? null : decodeCursor(query.cursor);
    return { limit: query.limit, ...(cursor ? { cursor } : {}) };
}
//# sourceMappingURL=pagination.js.map